/**
 * useSendReply — TanStack mutation for sending an email reply from the caller's OWN
 * connected mailbox (Story MC5, POST /mailbox/send).
 *
 * The reply is threaded onto its parent via `in_reply_to_communication_id`, so the
 * recorded outbound `communication` lands in the broker's Gesendet bucket — on success
 * we invalidate that live bucket so the sent reply appears (the mutation renders the
 * server's outbound row; no client-side domain logic). tenant + actor are resolved
 * server-side from the JWT (rule #1) — never on the wire.
 *
 * [Source: apps/web/src/features/communication-v2/hooks/useVnMessage.ts] (mutation + invalidate pattern)
 * [Source: apps/api/src/modules/mailbox-connect/mailbox-send.controller.ts] (the endpoint)
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MailboxSendMessageBody, MailboxSendResult } from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';

/** Send an email reply; on success refetch the live Gesendet bucket so the row appears. */
export function useSendReply() {
  const qc = useQueryClient();
  return useMutation<MailboxSendResult, Error, MailboxSendMessageBody>({
    mutationFn: (body) => apiClient.mailbox.send(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.communications('gesendet', { mode: 'live' }) });
    },
  });
}
